/**
 * Depth as a column of water you drag a mark down, not a number you type.
 *
 * Depth is the input that changes the output most, and it is the one a diver
 * has the strongest physical intuition for. The gradient is the actual light
 * you are descending through; the mark is where you will be shooting.
 */

import { useCallback, useRef } from "react";

import { MAX_DEPTH_M, type WaterType } from "../lib/conditions";

interface WaterColumnProps {
  depthM: number;
  water: WaterType;
  onChange: (depthM: number) => void;
}

const TICKS = [10, 20, 30, 40, 50];

export function WaterColumn({ depthM, water, onChange }: WaterColumnProps) {
  const ref = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const setFromPointer = useCallback(
    (clientY: number) => {
      const box = ref.current?.getBoundingClientRect();
      if (!box) return;
      const ratio = (clientY - box.top) / box.height;
      onChange(Math.max(0, Math.min(MAX_DEPTH_M, Math.round(ratio * MAX_DEPTH_M))));
    },
    [onChange],
  );

  return (
    <div
      ref={ref}
      role="slider"
      tabIndex={0}
      aria-label="Depth in metres"
      aria-valuemin={0}
      aria-valuemax={MAX_DEPTH_M}
      aria-valuenow={depthM}
      aria-valuetext={`${depthM} metres`}
      className={`water-column water-column--${water} relative h-[340px] w-full cursor-ns-resize overflow-hidden rounded-[2px] border-2 border-graphite touch-none`}
      onPointerDown={(e) => {
        dragging.current = true;
        e.currentTarget.setPointerCapture(e.pointerId);
        setFromPointer(e.clientY);
      }}
      onPointerMove={(e) => {
        if (dragging.current) setFromPointer(e.clientY);
      }}
      onPointerUp={() => {
        dragging.current = false;
      }}
      onKeyDown={(e) => {
        const step = e.shiftKey ? 5 : 1;
        let next = depthM;
        if (e.key === "ArrowUp" || e.key === "ArrowRight") next = Math.min(MAX_DEPTH_M, depthM + step);
        else if (e.key === "ArrowDown" || e.key === "ArrowLeft") next = Math.max(0, depthM - step);
        else if (e.key === "Home") next = 0;
        else if (e.key === "End") next = MAX_DEPTH_M;
        else return;
        e.preventDefault();
        onChange(next);
      }}
    >
      {TICKS.map((t) => (
        <div
          key={t}
          className="absolute inset-x-0 h-0 border-t border-white/30"
          style={{ top: `${(t / MAX_DEPTH_M) * 100}%` }}
        >
          <span className="absolute left-1 top-0.5 font-mono text-[9px] text-white/70">{t}</span>
        </div>
      ))}

      <div
        className="absolute -inset-x-0.5 h-0 border-t-[3px] border-smb"
        style={{ top: `${(depthM / MAX_DEPTH_M) * 100}%` }}
      >
        <b className="absolute right-[3px] top-1 font-display text-[21px] font-bold text-white [text-shadow:0_1px_3px_rgba(0,0,0,.7)]">
          {depthM} m
        </b>
      </div>
    </div>
  );
}
